import * as React from "react";

import { makeStyles } from "@material-ui/styles";
import Card from "@material-ui/core/Card";

import { Grid, CardContent } from "@material-ui/core";

import CircularProgress from "@material-ui/core/CircularProgress";
import ExcelIcon from "mdi-material-ui/FileExcel";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

//charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/pie";
import "echarts/lib/chart/gauge";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";

import { PageFooter, Button, BackButton } from "jazasoft";

import MuiTable from "../../../../components/MuiTable";
import CardHeader from "../../../../components/CardHeader";
import useReconciliationSummary from "./useReconciliationSummary";
import Table from "../../../../components/Table";
import { colorLightGreen } from "../../../../utils/colors";
import { DataType } from "../../../../helpers/reportHelper";
import { useDeepMemo } from "../../../../hooks";
import { humanizeNumber, isEmpty, toFixedValue } from "../../../../utils/helpers";
import { UOM } from "../../../../constants";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const columns1 = [
  { dataKey: "orderQty", title: "Order Qty" },
  { dataKey: "netCutQty", title: "Net Cut Qty" }, // cut-rejected
  { dataKey: "rejectedQty", title: "Rejected Qty" },
  { dataKey: "allocated", title: "Fabric Allocated", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "issued", title: "Fabric Issued", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "returned", title: "Returned to W/H", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "leftover", title: "Gross Leftover", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "netIssued", title: "Net Issued", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "blanketQty", title: "Testing Loss", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "totalConsumed", title: "Total Consumed", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` }
];

const columns2 = [
  { dataKey: "totalConsumed", title: "Total \nConsumed", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  {
    dataKey: "shortageExcess",
    title: "Net \nShortage/Excess",
    render: (value, _, row) => (isEmpty(value) ? "NA" : `${value} ${UomCodeFromValue[row.fabricUom] || ""}`)
  },
  { dataKey: "netConsumed", title: "Net \nConsumed", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "usedInMarker", title: "Used \nin Marker", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "endLoss", title: "End Loss", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "grossEndBits", title: "Gross \nEnd Bits", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  {
    dataKey: "endBitsUsedInPanel",
    title: "Panel Recut \n(End Bits)",
    render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}`
  },
  { dataKey: "netEndBits", title: "Net \nEnd Bits", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "rollUsedInPanel", title: "Panel Recut \n(Roll)", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "damaged", title: "Fabric \nDamage", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "netWastage", title: "Net \nWastage", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "missing", title: "Missed \nin Cutting", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "unused", title: "Unutilized \nby Cutting", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "blanketQty", title: "Testing Loss", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "grossWastage", title: "Gross \nWastage*", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` }
];

const useStyles = makeStyles({
  summary: {
    marginTop: "1.5em"
  },
  detail: {
    marginTop: "1.5em"
  },
  cutSummary: {
    marginTop: "1.5em"
  },
  marginVertical: {
    margin: "1em 0"
  }
});

const ReconciliationReportSummary = props => {
  const classes = useStyles();
  const { tabIdx } = props;

  const {
    summaryColumns,
    rows,
    columnsCutSummary,
    headerRows,
    rowsCutSummary,
    report,
    chartOptions,
    downloading,
    onDownload,
    selectedKey,
    setSelectedKey
  } = useReconciliationSummary(props);

  const rows1 = useDeepMemo(() => {
    if (isEmpty(report)) return [];

    const {
      fabricUom,
      orderQty = 0,
      cutQty = 0,
      rejectedQty = 0,
      blanketQty = 0,
      allocated = 0,
      issued = 0,
      returned = 0,
      blanketWeight = 0,
      allocatedWeight = 0,
      issuedWeight = 0,
      returnedWeight = 0
    } = report;

    const netCutQty = cutQty - (rejectedQty || 0);

    const leftover = allocated + returned - issued;
    const netIssued = issued - returned;
    const totalConsumed = issued - returned + blanketQty;

    const leftoverWeight = allocatedWeight + returnedWeight - issuedWeight;
    const netIssuedWeight = issuedWeight - returnedWeight;
    const totalConsumedWeight = issuedWeight - returnedWeight + blanketWeight;

    return [
      fabricUom === UOM.KILOGRAM.value
        ? {
          fabricUom,
          orderQty: humanizeNumber(orderQty),
          netCutQty: humanizeNumber(netCutQty),
          rejectedQty: humanizeNumber(rejectedQty || 0),
          blanketQty: humanizeNumber(toFixedValue(blanketWeight, 1)),
          allocated: humanizeNumber(toFixedValue(allocatedWeight, 1)),
          issued: humanizeNumber(toFixedValue(issuedWeight, 1)),
          returned: humanizeNumber(toFixedValue(returnedWeight, 1)),
          leftover: humanizeNumber(toFixedValue(leftoverWeight, 1)),
          netIssued: humanizeNumber(toFixedValue(netIssuedWeight, 1)),
          totalConsumed: humanizeNumber(toFixedValue(totalConsumedWeight, 1))
        }
        : {},
      fabricUom === UOM.METER.value
        ? {
          fabricUom: UOM.METER.value,
          orderQty: humanizeNumber(orderQty),
          netCutQty: humanizeNumber(netCutQty),
          rejectedQty: humanizeNumber(rejectedQty || 0),
          blanketQty: humanizeNumber(toFixedValue(blanketQty, 1)),
          allocated: humanizeNumber(toFixedValue(allocated, 1)),
          issued: humanizeNumber(toFixedValue(issued, 1)),
          returned: humanizeNumber(toFixedValue(returned, 1)),
          leftover: humanizeNumber(toFixedValue(leftover, 1)),
          netIssued: humanizeNumber(toFixedValue(netIssued, 1)),
          totalConsumed: humanizeNumber(toFixedValue(totalConsumed, 1))
        }
        : {},
      {
        orderQty: "100.0%",
        netCutQty: orderQty > 0 ? `${toFixedValue((netCutQty * 100) / orderQty, 1)}%` : "0.0%",
        rejectedQty: netCutQty > 0 ? `${toFixedValue((rejectedQty * 100) / netCutQty, 1)}%` : "0.0%",
        allocated: "100.0%",
        issued: allocated > 0 ? `${toFixedValue((issued * 100) / allocated, 2)}%` : "0.0%",
        returned: allocated > 0 ? `${toFixedValue((returned * 100) / allocated, 2)}%` : "0.0%",
        leftover: allocated > 0 ? `${toFixedValue((leftover * 100) / allocated, 2)}%` : "0.0%",
        netIssued: allocated > 0 ? `${toFixedValue((netIssued * 100) / allocated, 2)}%` : "0.0%",
        blanketQty: allocated + blanketQty > 0 ? `${toFixedValue((blanketQty * 100) / (allocated + blanketQty), 2)}%` : "0.0%",
        totalConsumed: allocated + blanketQty > 0 ? `${toFixedValue((totalConsumed * 100) / (allocated + blanketQty), 2)}%` : "0.0%"
      }
    ].filter(e => !isEmpty(e));
  }, [report]);

  const rows2 = useDeepMemo(() => {
    if (isEmpty(report)) return [];

    const {
      fabricUom,
      blanketQty = 0,
      issued = 0,
      returned = 0,
      usedInLay = 0,
      endLoss = 0,
      grossEndBits = 0,
      netEndBits = 0,
      damaged = 0,
      usedInPanel = 0,
      missing = 0,
      unused = 0,
      shortage = 0,
      excess = 0,
      blanketWeight = 0,
      issuedWeight = 0,
      returnedWeight = 0,
      usedInLayWeight = 0,
      endLossWeight = 0,
      grossEndBitsWeight = 0,
      netEndBitsWeight = 0,
      damagedWeight = 0,
      usedInPanelWeight = 0,
      missingWeight = 0,
      unusedWeight = 0
    } = report;

    const netWastage = endLoss + netEndBits + damaged + usedInPanel;
    const grossWastage = netWastage + missing + unused + blanketQty;
    const shortageExcess = shortage + excess;

    const totalConsumed = issued + blanketQty - returned;
    const netConsumed = totalConsumed + shortageExcess;
    const netIssued = issued - returned;
    const endBitsUsedInPanel = grossEndBits - netEndBits;
    const rollUsedInPanel = usedInPanel - endBitsUsedInPanel;

    const netWastageWeight = endLossWeight + netEndBitsWeight + damagedWeight + usedInPanelWeight;
    const grossWastageWeight = netWastageWeight + missingWeight + unusedWeight + blanketWeight;

    const totalConsumedWeight = issuedWeight + blanketWeight - returnedWeight;
    const netConsumedWeight = totalConsumedWeight + 0;
    const endBitsUsedInPanelWeight = grossEndBitsWeight - netEndBitsWeight;
    const rollUsedInPanelWeight = usedInPanelWeight - endBitsUsedInPanelWeight;

    return [
      fabricUom === UOM.KILOGRAM.value
        ? {
            fabricUom,
            totalConsumed: humanizeNumber(toFixedValue(totalConsumedWeight, 1)),
            shortageExcess: undefined,
            netConsumed: humanizeNumber(toFixedValue(netConsumedWeight, 1)),
            usedInMarker: humanizeNumber(toFixedValue(usedInLayWeight - endLossWeight, 1)),
            endLoss: humanizeNumber(toFixedValue(endLossWeight, 1)),
            netEndBits: humanizeNumber(toFixedValue(netEndBitsWeight, 1)),
            endBitsUsedInPanel: humanizeNumber(toFixedValue(endBitsUsedInPanelWeight, 1)),
            grossEndBits: humanizeNumber(toFixedValue(grossEndBitsWeight, 1)),
            damaged: humanizeNumber(toFixedValue(damagedWeight, 1)),
            rollUsedInPanel: humanizeNumber(toFixedValue(rollUsedInPanelWeight, 1)),
            netWastage: humanizeNumber(toFixedValue(netWastageWeight, 1)),
            missing: humanizeNumber(toFixedValue(missingWeight, 1)),
            unused: humanizeNumber(toFixedValue(unusedWeight, 1)),
            blanketQty: humanizeNumber(toFixedValue(blanketWeight, 1)),
            grossWastage: humanizeNumber(toFixedValue(grossWastageWeight, 1))
          }
        : {},
      fabricUom === UOM.METER.value
        ? {
            fabricUom: UOM.METER.value,
            totalConsumed: humanizeNumber(toFixedValue(totalConsumed, 1)),
            shortageExcess: fabricUom === UOM.KILOGRAM.value ? undefined : humanizeNumber(toFixedValue(shortageExcess, 1)),
            netConsumed: humanizeNumber(toFixedValue(netConsumed, 1)),
            usedInMarker: humanizeNumber(toFixedValue(usedInLay - endLoss, 1)),
            endLoss: humanizeNumber(toFixedValue(endLoss, 1)),
            netEndBits: humanizeNumber(toFixedValue(netEndBits, 1)),
            endBitsUsedInPanel: humanizeNumber(toFixedValue(endBitsUsedInPanel, 1)),
            grossEndBits: humanizeNumber(toFixedValue(grossEndBits, 1)),
            damaged: humanizeNumber(toFixedValue(damaged, 1)),
            rollUsedInPanel: humanizeNumber(toFixedValue(rollUsedInPanel, 1)),
            netWastage: humanizeNumber(toFixedValue(netWastage, 1)),
            missing: humanizeNumber(toFixedValue(missing, 1)),
            unused: humanizeNumber(toFixedValue(unused, 1)),
            blanketQty: humanizeNumber(toFixedValue(blanketQty, 1)),
            grossWastage: humanizeNumber(toFixedValue(grossWastage, 1))
          }
        : {},
      {
        totalConsumed: "100.0%",
        shortageExcess:
          fabricUom === UOM.KILOGRAM.value ? undefined : netIssued > 0 ? `${toFixedValue((shortageExcess * 100) / totalConsumed, 2)}%` : "0.0%",
        netConsumed: "100.0%",
        usedInMarker: netConsumed > 0 ? `${toFixedValue(((usedInLay - endLoss) * 100) / netConsumed, 2)}%` : "0.0%",
        endLoss: netConsumed > 0 ? `${toFixedValue((endLoss * 100) / netConsumed, 2)}%` : "0.0%",
        grossEndBits: netConsumed > 0 ? `${toFixedValue((grossEndBits * 100) / netConsumed, 2)}%` : "0.0%",
        endBitsUsedInPanel: netConsumed > 0 ? `${toFixedValue((endBitsUsedInPanel * 100) / netConsumed, 2)}%` : "0.0%",
        netEndBits: netConsumed > 0 ? `${toFixedValue((netEndBits * 100) / netConsumed, 2)}%` : "0.0%",
        rollUsedInPanel: netConsumed > 0 ? `${toFixedValue((rollUsedInPanel * 100) / netConsumed, 2)}%` : "0.0%",
        damaged: netConsumed > 0 ? `${toFixedValue((damaged * 100) / netConsumed, 2)}%` : "0.0%",
        usedInPanel: netConsumed > 0 ? `${toFixedValue((usedInPanel * 100) / netConsumed, 2)}%` : "0.0%",
        netWastage: netConsumed > 0 ? `${toFixedValue((netWastage * 100) / netConsumed, 2)}%` : "0.0%",
        missing: netConsumed > 0 ? `${toFixedValue((missing * 100) / netConsumed, 2)}%` : "0.0%",
        unused: netConsumed > 0 ? `${toFixedValue((unused * 100) / netConsumed, 2)}%` : "0.0%",
        blanketQty: netConsumed > 0 ? `${toFixedValue((blanketQty * 100) / netConsumed, 2)}%` : "0.0%",
        grossWastage: netConsumed > 0 ? `${toFixedValue((grossWastage * 100) / netConsumed, 2)}%` : "0.0%"
      }
    ].filter(e => !isEmpty(e));
  }, [report]);

  const onSummaryClick = row => {
    if (row?.isParent) return;
    setSelectedKey(row?.key);
  };

  if (tabIdx !== 0) return <div />;

  return (
    <div>
      <div className={classes.summary}>
        <MuiTable
          title="Order Summary"
          columns={summaryColumns}
          rows={rows}
          searchKeys={["style", "color"]}
          onRowClick={onSummaryClick}
          rowStyle={({ row }) => (selectedKey === row?.key ? { backgroundColor: "#BFDDF8" } : {})}
          idKey="key"
          parentIdKey="parentKey"
          defaultExpanded
          pageable
          i18nKey={`resources.report.drr.columns`}
        />
      </div>

      <div id="chart-container" className={classes.marginVertical}>
        {Object.keys(chartOptions).length > 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} id="row-1">
              <Card className={classes.border}>
                <CardHeader title="Fabric Utilization Summary" />
                <CardContent style={{ padding: "0 0 8px 0" }}>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={chartOptions[DataType.FABRIC_UTILIZATION_SUMMARY] || {}}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} id="row-2">
              <Card className={classes.border}>
                <CardHeader title="Wastage Summary" />
                <CardContent style={{ padding: "0 0 8px 0" }}>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={chartOptions[DataType.WASTAGE_SUMMMARY] || {}}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} id="row-3">
              <Card className={classes.border}>
                <CardHeader title="Consumption Summary" />
                <CardContent style={{ padding: "0 0 8px 16px" }}>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={chartOptions[DataType.CONSUMPTION_SUMMARY] || {}}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {Object.keys(chartOptions).length > 0 && (
          <Grid id="row-4" container spacing={2}>
            <Grid item xs={12} sm={4} id="row-3">
              <Card className={classes.border}>
                <CardHeader title="Cut Summary" />
                <CardContent style={{ padding: "0 0 8px 16px" }}>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={chartOptions[DataType.CUT_SUMMARY] || {}}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} id="marker">
              <Card className={classes.border}>
                <CardHeader title="Marker Efficiency" />
                <CardContent style={{ padding: "0 0 8px 16px" }}>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={chartOptions[DataType.MARKER_EFFICIENCY] || {}}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </div>

      <div style={{ marginTop: "1em" }}>
        <MuiTable toolbar title={"Fabric Summary"} columns={columns1} rows={rows1} />
      </div>

      <div style={{ marginTop: "1em" }}>
        <MuiTable title={"Fabric Used Details"} toolbar columns={columns2} rows={rows2} />
      </div>

      <Card className={classes.cutSummary}>
        <CardHeader title="Size Wise Cut Qty" />
        <PerfectScrollbar>
          <Table
            columns={columnsCutSummary}
            headerRows={headerRows}
            rows={rowsCutSummary}
            headerCellStyle={column => ({
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              backgroundColor: "#C6D0DA"
            })}
            cellStyle={({ column, record }) =>
              record.name === "Total"
                ? {
                    fontWeight: "bold",
                    borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                    borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                  }
                : {
                    borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                    borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                  }
            }
            i18nKey={`resources.report.drr.columns`}
          />
        </PerfectScrollbar>
      </Card>

      <PageFooter>
        <Button
          label="Download"
          variant="outlined"
          disabled={downloading}
          onClick={onDownload}
          style={{ color: colorLightGreen.main, borderColor: colorLightGreen.main }}>
          {downloading ? <CircularProgress size={25} thickness={2} color="secondary" /> : <ExcelIcon />}
        </Button>
        <BackButton variant="outlined" style={{ marginLeft: "1.5em" }} />
      </PageFooter>
    </div>
  );
};

export default ReconciliationReportSummary;
